// Components==============
import { faHexagon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { useMediaQ } from 'hooks/useMediaQ';
import { usePermission } from 'hooks/usePermission';
import { useTranslate } from 'hooks/useTranslate';
import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import { NavLink } from 'react-router-dom';
import { TranslateType } from 'typings';
import { GlobalSideNavContext } from 'utilities/GlobalContext/GloabalSideNav';
import {
  GlobalOpenNavItemsContext,
  initialOpenNavItems,
} from 'utilities/GlobalContext/GlobalOpenNavItems';
import { navItems, NavParents } from '../navItems';
import './MenuItems.module.scss';
// =========================

type Item = {
  name: TranslateType;
  icon: any;
  id: NavParents;
  to?: string;
  image?: string;
  beta?: string;
  children?: {
    to: string;
    id: string;
    name: TranslateType;
  }[];
};

const MenuItem = ({ item }: { item: Item }) => {
  const { openNavItems, setOpenNavItems } = useContext(
    GlobalOpenNavItemsContext
  );
  const { sideNavIsOpen, setSideNavIsOpen } = useContext(GlobalSideNavContext);

  const { location, push } = useHistory();

  const isOpen = openNavItems[item.id];
  const isActive = (id: string) => {
    const parts = location.pathname.split('/');
    const folder = parts[parts.length - 2];
    const page = parts[parts.length - 1];

    return folder === id || page === id;
  };

  function handleClick() {
    if (!sideNavIsOpen) setSideNavIsOpen(true);
    setOpenNavItems((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
  }

  const x = sideNavIsOpen ? 0 : -8;

  useEffect(() => {
    if (isActive(item.id))
      setOpenNavItems({ ...initialOpenNavItems, [item.id]: true });
  }, [location]);

  useEffect(() => {
    if (!sideNavIsOpen) setOpenNavItems(initialOpenNavItems);
  }, [sideNavIsOpen]);

  return (
    <div>
      <motion.button
        styleName={isActive(item.id) ? 'active' : ''}
        onClick={() => (item?.to ? push(item?.to) : handleClick())}
        type="button"
        animate={{ x }}
        initial={{ x }}
      >
        {typeof item.icon === 'string' ? (
          <img
            src={item.icon}
            alt={item.name as string}
            styleName={!isActive(item.id) ? 'inactive-image' : ''}
          />
        ) : (
          <FontAwesomeIcon icon={item.icon} />
        )}
        {sideNavIsOpen && item.name}
        {typeof item.beta === 'string' && (
          <img
            src={item.beta}
            alt={item.name as string}
            styleName={!isActive(item.id) ? 'beta' : ''}
          />
        )}
      </motion.button>
      {isOpen && item?.children && (
        <div styleName="children">
          {item.children.map((child, index) => (
            <NavLink
              key={index}
              to={child.to}
              styleName={isActive(child.id) ? 'active' : ''}
            >
              {child.name}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export default function MenuItems() {
  const translate = useTranslate();
  const isTeacher = usePermission(['Docent', 'super-admin']);
  const isExtern = usePermission(['Extern']);

  const items = navItems(translate, isTeacher, isExtern);

  const { sideNavIsOpen } = useContext(GlobalSideNavContext);

  const query = useMediaQ('max', 1024);

  return (
    <div
      styleName="items"
      style={{ justifyItems: sideNavIsOpen ? 'start' : 'center' }}
    >
      {items.map((item, i) => (
        <MenuItem item={item} key={i} />
      ))}
      {query && (
        <MenuItem
          item={{
            name: translate('general.terms.ar-scanner'),
            image: '/images/scan-token-2.svg',
            icon: faHexagon,
            id: 'scanner',
            beta: '/images/beta-white.svg',
            children: [
              {
                to: '/scan',
                name: translate('general.terms.activity-scanner'),
                id: 'scan',
              },
              {
                to: '/mlscan/noTheme',
                name: translate('general.terms.process-scanner'),
                id: 'MLScan',
              },
            ],
          }}
        />
      )}
    </div>
  );
}
